// @flow
import { type ContextRouter } from 'react-router-dom';
import * as React from 'react';

import AnalysisContent from './AnalysisContent';
import AppContext from '../../../AppContext';
import CompletionPage from '../../common/CompletionPage';
import Constants from '../../ice/fields/Constants';
import IceContext from '../../ice/fields/IceContext';
import Menu from '../../ice/fields/Menu';
import ResultContent from './ResultContent';
import SettingContent from './SettingContent';
import SimulationContent from './SimulationContent';
import WagonSettingContent from './WagonSettingContent';
import WeatherContent from './WeatherContent';
import './ice-field-advance.css';

type Page = 'wagon-setting' | 'result' | 'weather' | 'setting' | 'simulation' | 'analysis' | 'complete';

type Props = {
  ...ContextRouter,
  className: string
};

type State = {
  page: Page,
  wagon: number,
  iceContexts: IceContext[],
  date: number,
  analysisCtxIndex: number | null
};

const impossible = <T>(t: empty): T => {
  throw new Error(`Unknown ${t}`);
};

const previousPage = (page: Page): Page => {
  switch (page) {
    case 'wagon-setting':
      return 'wagon-setting';
    case 'result':
      return 'wagon-setting';
    case 'weather':
      return 'result';
    case 'setting':
      return 'weather';
    case 'simulation':
      return 'simulation';
    case 'analysis':
      return 'result';
    case 'complete':
      return 'result';
    default:
      return impossible(page);
  }
};

export default class IceFieldAdvanceChallengePage extends React.Component<Props, State> {
  static defaultProps = { className: '' };
  static contextType = AppContext;

  temperatures: number[];

  constructor(props: Props) {
    super(props);
    this.temperatures = Constants.getRandomTemperatures();

    const iceContexts = Constants.getRandomWeekdays().map((weekday, index) => {
      const iceContext = new IceContext(Math.random());
      iceContext.weekday = weekday;
      iceContext.temperature = this.temperatures[index];
      return iceContext;
    });

    this.state = {
      page: 'wagon-setting',
      wagon: 0,
      iceContexts: iceContexts,
      date: 0,
      analysisCtxIndex: null
    };

    (this: any).handleNextClick = this.handleNextClick.bind(this);
    (this: any).handleAnalysisClick = this.handleAnalysisClick.bind(this);
    (this: any).handlePreviousClick = this.handlePreviousClick.bind(this);
    (this: any).handleWagonChange = this.handleWagonChange.bind(this);
    (this: any).startNewDay = this.startNewDay.bind(this);
    (this: any).onUpdateResults = this.onUpdateResults.bind(this);
    (this: any).handlePlaceChange = this.handlePlaceChange.bind(this);
    (this: any).handleIceChange = this.handleIceChange.bind(this);
    (this: any).handlePriceChange = this.handlePriceChange.bind(this);
    (this: any).handleStockNumChange = this.handleStockNumChange.bind(this);
  }

  componentDidMount() {
    this.context.soundController.bgmPlay('proc_field02', true);
  }

  handleNextClick(): void {
    let nextPage = 'wagon-setting';
    switch (this.state.page) {
      case 'wagon-setting':
        nextPage = 'result';
        this.context.soundController.bgmPlay('proc_field02', true);
        break;
      case 'result':
        nextPage = 'weather';
        this.context.soundController.bgmPlay('proc_field02', true);
        break;
      case 'weather':
        nextPage = 'setting';
        this.context.soundController.bgmPlay('proc_field02', true);
        break;
      case 'setting':
        nextPage = 'simulation';
        this.context.soundController.bgmPlay('proc_field01', true);
        break;
      case 'simulation':
        nextPage = 'analysis';
        this.context.soundController.bgmPlay('proc_field02', true);
        break;
      case 'analysis':
        nextPage = 'result';
        this.context.soundController.bgmPlay('proc_field02', true);
        break;
      case 'complete':
        this.context.soundController.bgmPlay('proc_field02', true);
        nextPage = 'complete';
        break;
      default:
        impossible(this.state.page);
    }
    if (nextPage === 'weather' && this.state.date > 3) {
      nextPage = 'complete';
    }
    this.setState({ page: nextPage, analysisCtxIndex: null });
  }

  handleAnalysisClick(ctxIndex: number): void {
    this.setState({ page: 'analysis', analysisCtxIndex: ctxIndex });
  }

  handlePreviousClick(): void {
    this.setState({ page: previousPage(this.state.page) });
  }

  handleWagonChange(wagon: number): void {
    this.setState({ wagon });
  }

  startNewDay(): void {
    if (this.currentIceContext.started) {
      this.setState(({ date }) => ({ date: date + 1 }), this.handleNextClick());
    } else {
      this.handleNextClick();
    }
  }

  onUpdateResults(results: Array<any>) {
    const iceCtx = this.currentIceContext;
    for (const result of results) {
      iceCtx.addSalesData(result.salesData);
      iceCtx.addCustomersData(result.customersData);
      iceCtx.addPurchasersData(result.purchasersData);
      iceCtx.addPriceTransitionData(result.priceTransitionData);
    }

    this.forceUpdate();
  }

  handlePlaceChange(place: number) {
    const iceCtx = this.currentIceContext;
    iceCtx.place = place;
    this.forceUpdate();
  }

  handleIceChange(ice: number) {
    const iceCtx = this.currentIceContext;
    iceCtx.ice = ice;
    this.forceUpdate();
  }

  handlePriceChange(price: number) {
    const iceCtx = this.currentIceContext;
    iceCtx.price = price;
    this.forceUpdate();
  }

  handleStockNumChange(stockNum: number) {
    const iceCtx = this.currentIceContext;
    iceCtx.stockNum = stockNum;
    this.forceUpdate();
  }

  get currentIceContext(): IceContext {
    return this.state.iceContexts[this.state.date];
  }

  currentPage(): React.Node {
    switch (this.state.page) {
      case 'wagon-setting':
        return (
          <WagonSettingContent
            wagon={this.state.wagon}
            onWagonChange={this.handleWagonChange}
            onNextClick={this.handleNextClick}
          />
        );
      case 'result':
        return (
          <ResultContent
            iceContexts={this.state.iceContexts}
            date={this.state.date}
            wagon={this.state.wagon}
            title={'チャレンジ'}
            onNextClick={this.handleNextClick}
            onAnalysisClick={this.handleAnalysisClick}
          />
        );
      case 'weather':
        return (
          <WeatherContent
            title={`${this.state.date + 1} 回目`}
            subTitle={Constants.WEEKDAY_LABELS_ICE_ADV[this.state.iceContexts[this.state.date].weekday]}
            iceCtx={this.currentIceContext}
            onNextClick={this.handleNextClick}
            onPreviousClick={this.handlePreviousClick}
          />
        );
      case 'setting':
        return (
          <SettingContent
            wagon={this.state.wagon}
            iceCtx={this.currentIceContext}
            title={this._getTitle()}
            onNextClick={this.handleNextClick}
            onPreviousClick={this.handlePreviousClick}
            onPlaceChange={this.handlePlaceChange}
            onIceChange={this.handleIceChange}
            onPriceChange={this.handlePriceChange}
            onStockNumChange={this.handleStockNumChange}
          />
        );
      case 'simulation':
        return (
          <SimulationContent
            wagon={this.state.wagon}
            customers={this.currentIceContext.customers}
            iceCtx={this.currentIceContext}
            onPriceChange={this.handlePriceChange}
            onNextClick={this.handleNextClick}
            onUpdateResults={this.onUpdateResults}
            onStockNumChange={this.handleStockNumChange}
          />
        );
      case 'analysis':
        return (
          <AnalysisContent
            wagon={this.state.wagon}
            iceCtxs={this.state.iceContexts}
            iceCtxIndex={this.state.analysisCtxIndex === null ? this.state.date : this.state.analysisCtxIndex}
            onNextClick={this.state.analysisCtxIndex === null ? this.startNewDay : null}
            onPreviousClick={this.state.analysisCtxIndex === null ? null : this.handlePreviousClick}
          />
        );
      case 'complete':
        return (
          <CompletionPage
            history={this.props.history}
            kind={'ice-a'}
            totalScore={this.state.iceContexts.reduce((acc, ctx) => {
              if (ctx.totalSales === 0) {
                return acc + ctx.totalSales;
              } else {
                return acc + (ctx.totalSales - ctx.stockNum * Constants.DEFAULT_PRICE);
              }
            }, 0)}
          />
        );
      default:
        return impossible(this.state.page);
    }
  }

  render() {
    const showMenu = ['simulation', 'weather'].every(p => p !== this.state.page);
    let menuText = undefined;
    if (this.state.page === 'result') {
      menuText = 'いちらん';
    } else if (this.state.page === 'setting') {
      menuText = this._getTitle();
    } else if (this.state.page === 'complete') {
      menuText = 'さいしゅうけっか';
    } else if (this.state.page === 'wagon-setting') {
      menuText = 'アイスクリームを仕入れて売ろう';
    } else if (this.state.page === 'analysis') {
      if (this.state.analysisCtxIndex === null) {
        menuText = this._getTitle();
      } else {
        menuText = `${Constants.WEEKDAY_LABELS_ICE_ADV[this.state.iceContexts[this.state.analysisCtxIndex].weekday]}`;
      }
      menuText += ' けっか';
    }
    return (
      <div
        id={'proc-ice-fields-root-page'}
        className={`proc-ice-field-page proc-ice-advanced-field-page h100 ${
          this.state.page === 'weather' ? 'secondary300-bg-color weather' : 'tertiary100-bg-color'
        }`}
      >
        {showMenu && (
          <Menu
            exitLink={'/ice-a/fields'}
            query={'?skip_intro=true'}
            title={''}
            subTitle={'アイスクリームを仕入れて売ろう'}
            menuText={menuText}
          />
        )}
        {this.currentPage()}
      </div>
    );
  }

  _getTitle() {
    return `${Constants.WEEKDAY_LABELS_ICE_ADV[this.currentIceContext.weekday]}`;
  }
}
